"use client";

import { useState } from "react";
import { DataTable, SelectField } from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { CviStrings } from "@/components/tools/content-validity-index-calculator";
import { t } from "@/lib/i18n/interpolate";
import { logChoose } from "@/lib/stats/special";
import {
  type CviItem,
  type KappaRating,
  LYNN_TABLE,
  lynnCriterion,
} from "@/lib/tool-kit/research-design/cvi";
import {
  decimals,
  type NumberFormatter,
  superscript,
} from "@/lib/tool-kit/research-design/format";

const RATING_KEYS: Record<KappaRating, keyof CviStrings> = {
  excellent: "ratingExcellent",
  good: "ratingGood",
  fair: "ratingFair",
  poor: "ratingPoor",
};

export function ratingLabel(rating: KappaRating, strings: CviStrings): string {
  return strings[RATING_KEYS[rating]] as string;
}

/** "Meets (5 of 6)" — the Lynn column of the item table. */
export function lynnText(item: CviItem, strings: CviStrings): string {
  if (!item.lynn) {
    return strings.lynnTooFew;
  }
  return t(item.meetsLynn ? strings.lynnMet : strings.lynnMissed, {
    agree: item.lynn.agree,
    experts: item.lynn.experts,
  });
}

function lynnSentence(
  item: CviItem,
  strings: CviStrings,
  fmt: NumberFormatter
): string {
  if (!item.lynn) {
    return strings.workingNoLynn;
  }
  return t(
    item.meetsLynn ? strings.workingLynnMet : strings.workingLynnMissed,
    {
      experts: fmt(item.lynn.experts),
      agree: fmt(item.lynn.agree),
      have: fmt(item.agree),
    }
  );
}

/** The I-CVI, Pc and k* worked through for one item the reader picks. */
export function CviWorking({
  items,
  itemNames,
  strings,
  fmt,
}: {
  items: readonly (CviItem | null)[];
  itemNames: readonly string[];
  strings: CviStrings;
  fmt: NumberFormatter;
}) {
  const firstShort = items.findIndex(
    (item) => item !== null && !item.universal
  );
  const [picked, setPicked] = useState<string | null>(null);
  const index =
    picked !== null && items[Number(picked)]
      ? Number(picked)
      : Math.max(0, firstShort);
  const item = items[index] ?? null;
  const options = itemNames.flatMap((name, i) =>
    items[i] ? [{ value: String(i), label: name }] : []
  );
  if (!item || options.length === 0) {
    return null;
  }
  const rest = item.experts - item.agree;
  return (
    <div className={`${CARD} space-y-3`}>
      <div className="max-w-xs">
        <SelectField
          id="cvi-working"
          label={strings.workingFor}
          onChange={setPicked}
          options={options}
          value={String(index)}
        />
      </div>
      <div className="space-y-2 font-mono text-muted-foreground text-sm leading-relaxed">
        <p>
          {t(strings.workingIcvi, {
            agree: fmt(item.agree),
            experts: fmt(item.experts),
            icvi: decimals(fmt, item.icvi, 3),
          })}
        </p>
        <p>
          {t(strings.workingPc, {
            experts: fmt(item.experts),
            agree: fmt(item.agree),
            rest: fmt(rest),
            power: superscript(item.experts),
            ways: fmt(
              Math.round(Math.exp(logChoose(item.experts, item.agree)))
            ),
            half: fmt(0.5 ** item.experts, { maximumSignificantDigits: 6 }),
            pc: decimals(fmt, item.pc, 3),
          })}
        </p>
        <p>
          {t(strings.workingKappa, {
            icvi: decimals(fmt, item.icvi, 3),
            pc: decimals(fmt, item.pc, 3),
            kappa: decimals(fmt, item.kappa),
          })}
        </p>
      </div>
      <p className="text-foreground text-sm">
        {t(strings.workingRating, {
          rating: ratingLabel(item.rating, strings),
        })}
      </p>
      <p className="text-foreground text-sm">
        {lynnSentence(item, strings, fmt)}
      </p>
    </div>
  );
}

/** Lynn's (1986) Table 2, with the reader's panel size marked. */
export function LynnTable({
  experts,
  strings,
  fmt,
}: {
  experts: number;
  strings: CviStrings;
  fmt: NumberFormatter;
}) {
  const beyond = experts > 10 ? lynnCriterion(experts) : null;
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.lynnHeading}</h3>
      <DataTable
        head={[strings.colPanel, strings.colMustAgree, strings.colMinimum]}
        rows={LYNN_TABLE.map((row) => [
          row.experts === experts
            ? t(strings.yourPanel, { experts: fmt(row.experts) })
            : fmt(row.experts),
          fmt(row.agree),
          decimals(fmt, row.agree / row.experts),
        ])}
      />
      {beyond ? (
        <p className={HINT}>
          {t(strings.lynnLarger, {
            agree: fmt(beyond.agree),
            experts: fmt(beyond.experts),
          })}
        </p>
      ) : null}
    </div>
  );
}
